import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/lib/auth.jsx';

import LandingPage from '@/pages/Landing';
import LoginPage from '@/pages/Login';
import SignupPage from '@/pages/Signup';
import ForgotPasswordPage from '@/pages/ForgotPassword';
import ResetPasswordPage from '@/pages/ResetPassword';
import PricingPage from '@/pages/Pricing';
import BillingSuccessPage from '@/pages/BillingSuccess';
import DashboardPage from '@/pages/Dashboard';
import LeadFinderPage from '@/pages/LeadFinder';
import LeadDetailPage from '@/pages/LeadDetail';
import PipelinePage from '@/pages/Pipeline';
import QuotesListPage from '@/pages/QuotesList';
import EstimateBuilderPage from '@/pages/EstimateBuilder';
import BillingPage from '@/pages/Billing';
import TeamPage from '@/pages/Team';
import SettingsPage from '@/pages/Settings';
import MarketTrendsPage from '@/pages/MarketTrends';
import FeedbackPage from '@/pages/Feedback';
import PublicQuotePage from '@/pages/PublicQuote';
import AcceptInvitePage from '@/pages/AcceptInvite';
import TermsPage from '@/pages/Terms';
import PrivacyPage from '@/pages/Privacy';
import RefundPolicyPage from '@/pages/RefundPolicy';
import AcceptableUsePage from '@/pages/AcceptableUse';
import ContractorAddendumPage from '@/pages/ContractorAddendum';
import DisclosuresPage from '@/pages/Disclosures';
import { AppShell } from '@/components/AppShell';
import { CookieBanner } from '@/components/CookieBanner';
import { PaywallModal } from '@/components/PaywallModal';
import { SupportChat } from '@/components/SupportChat';
import { FeedbackWidget } from '@/components/FeedbackWidget';
import { TrialOnboardingTour } from '@/components/TrialOnboardingTour';

function RequireAuth({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-muted-foreground">Loading...</div>
            </div>
        );
    }
    if (!user) {
        return <Navigate to="/login" replace />;
    }
    return children;
}

function ShellRoute({ children }) {
    return (
        <RequireAuth>
            <AppShell>{children}</AppShell>
        </RequireAuth>
    );
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/signup" element={<SignupPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/pricing" element={<PricingPage />} />
                        <Route path="/billing/success" element={<RequireAuth><BillingSuccessPage /></RequireAuth>} />
                        <Route path="/q/:token" element={<PublicQuotePage />} />
                        <Route path="/invite/:token" element={<AcceptInvitePage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/refund-policy" element={<RefundPolicyPage />} />
                        <Route path="/acceptable-use" element={<AcceptableUsePage />} />
                        <Route path="/contractor-addendum" element={<ContractorAddendumPage />} />
                        <Route path="/disclosures" element={<DisclosuresPage />} />

                        <Route path="/app" element={<ShellRoute><DashboardPage /></ShellRoute>} />
                        <Route path="/app/dashboard" element={<ShellRoute><DashboardPage /></ShellRoute>} />
                        <Route path="/app/leads" element={<ShellRoute><LeadFinderPage /></ShellRoute>} />
                        <Route path="/app/leads/pipeline" element={<ShellRoute><PipelinePage /></ShellRoute>} />
                        <Route path="/app/leads/:propertyKey" element={<ShellRoute><LeadDetailPage /></ShellRoute>} />
                        <Route path="/app/outreach" element={<Navigate to="/app/leads/pipeline" replace />} />
                        <Route path="/app/quotes" element={<ShellRoute><QuotesListPage /></ShellRoute>} />
                        <Route path="/app/quotes/:quoteId" element={<ShellRoute><EstimateBuilderPage /></ShellRoute>} />
                        <Route path="/app/billing" element={<ShellRoute><BillingPage /></ShellRoute>} />
                        <Route path="/app/team" element={<ShellRoute><TeamPage /></ShellRoute>} />
                        <Route path="/app/settings" element={<ShellRoute><SettingsPage /></ShellRoute>} />
                        <Route path="/app/market-trends" element={<ShellRoute><MarketTrendsPage /></ShellRoute>} />
                        <Route path="/app/feedback" element={<ShellRoute><FeedbackPage /></ShellRoute>} />
                    </Routes>
                    <CookieBanner />
                    <PaywallModal />
                    <SupportChat />
                    <FeedbackWidget />
                    <TrialOnboardingTour />
                </BrowserRouter>
                <Toaster richColors closeButton position="top-right" />
            </AuthProvider>
        </div>
    );
}

export default App;
